// B-72 — "Sábados laborales", under the Calendario: the Saturdays a client works.
//
// Only a client whose offices do not close on Saturdays by default has such a list (Bancamía);
// the backend says so with `usesOpenDates`, and for every other client this renders nothing.
//
// Each one listed here opens every office's day: the scheduler and "Procesar el día" read this
// list (`client_open_dates` in the backend) beside the office calendar, so adding a Saturday needs
// no deploy and removing one stops it being scheduled. Two ways in:
//
// - **From a picture** of the client's list ("Cargar desde imagen"): perlita reads the dates and
//   the screen shows them to confirm or correct — nothing is saved until "Guardar".
// - **By hand**: add, change or remove one.
//
// A Saturday that already has closings cannot be removed or moved (the backend's 409 says why, and
// the buttons say it first). Changing the list needs `cierres.daily.run` on the whole client; anyone
// who may see the client may read it.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarCheck,
  Check,
  Circle,
  History,
  ImageUp,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
  cn,
} from "@mantis-sas/ui";

import {
  addCierreOpenDates,
  listCierreOpenDates,
  previewCierreOpenDates,
  removeCierreOpenDate,
  updateCierreOpenDate,
} from "../../../ApiCalls/mantisBusiness";
import {
  errorDeSabados,
  fechaLarga,
  fechasAGuardar,
  filasDeVistaPrevia,
  motivoBloqueo,
  origenDeSabado,
  partesDeFecha,
  pasosDeLectura,
  revisarFecha,
  revisarVistaPrevia,
  separarSabados,
} from "../../../utils/cierreSabados";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

const VISTAS = [
  { clave: "proximos", titulo: "Próximos" },
  { clave: "pasados", titulo: "Pasados" },
  { clave: "historial", titulo: "Historial" },
];

const campo =
  "h-9 rounded-[8px] border bg-card px-2.5 text-[12.5px] text-brinks-foreground outline-none focus:border-brinks-border-strong disabled:opacity-60";

const cuando = (instante) =>
  instante
    ? new Date(instante).toLocaleDateString("es-CO", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "America/Bogota",
      })
    : "";

function Aviso({ tono = "error", children }) {
  if (!children) return null;
  return (
    <p
      role={tono === "error" ? "alert" : "status"}
      className={cn(
        "rounded-[8px] border px-2.5 py-1.5 text-[12.5px]",
        tono === "error" &&
          "border-brinks-danger-border bg-brinks-danger-soft text-brinks-danger",
        tono === "ok" &&
          "border-brinks-success-border bg-brinks-success-soft text-brinks-success",
        tono === "aviso" &&
          "border-brinks-warning-border bg-brinks-warning-soft text-brinks-warning",
      )}
    >
      {children}
    </p>
  );
}

// The form a Saturday is typed in — to add one, or to change one in its row.
function FormularioFecha({
  inicial = "",
  notaInicial = "",
  existentes,
  hoy,
  guardando,
  onGuardar,
  onCancelar,
  etiqueta,
  fechaFija,
}) {
  const [fecha, setFecha] = useState(inicial);
  const [nota, setNota] = useState(notaInicial);
  const revision = fecha ? revisarFecha(fecha, { existentes, hoy }) : null;
  const sinCambios =
    fecha === inicial && nota.trim() === (notaInicial || "").trim();

  return (
    <div className="grid gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="date"
          aria-label="Fecha del sábado"
          value={fecha}
          disabled={guardando || Boolean(fechaFija)}
          title={fechaFija || undefined}
          onChange={(e) => setFecha(e.target.value)}
          className={cn(campo, "w-[150px]")}
        />
        <input
          type="text"
          aria-label="Nota"
          placeholder="Nota (opcional)"
          maxLength={500}
          value={nota}
          disabled={guardando}
          onChange={(e) => setNota(e.target.value)}
          className={cn(campo, "min-w-[160px] flex-1")}
        />
        <Button
          size="sm"
          className="h-9 gap-1.5"
          disabled={guardando || !revision?.ok || sinCambios}
          onClick={() => onGuardar(fecha, nota)}
        >
          {guardando ? <Spinner size="sm" /> : <Check className="h-4 w-4" />}{" "}
          {etiqueta}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-9"
          disabled={guardando}
          onClick={onCancelar}
        >
          Cancelar
        </Button>
      </div>
      {revision && !revision.ok ? (
        <span className="text-[11.5px] text-brinks-danger">
          {revision.mensaje}
        </span>
      ) : null}
      {revision?.ok && revision.aviso ? (
        <span className="text-[11.5px] text-brinks-warning">
          {revision.aviso}
        </span>
      ) : null}
      {fechaFija ? (
        <span className="text-[11.5px] text-brinks-muted-foreground">
          {fechaFija} Solo se puede cambiar la nota.
        </span>
      ) : null}
      {revision?.ok && !revision.aviso && !fechaFija ? (
        <span className="text-[11.5px] text-brinks-muted-foreground">
          {fechaLarga(fecha)}
        </span>
      ) : null}
    </div>
  );
}

const ICONO_PASO = {
  pending: <Circle className="h-3.5 w-3.5 text-brinks-muted-foreground" />,
  running: <Spinner size="sm" />,
  done: <Check className="h-3.5 w-3.5 text-brinks-success" />,
  failed: <X className="h-3.5 w-3.5 text-brinks-danger" />,
};

// While the AI reads the picture: each step the backend reports, checked off as it ends, and the
// seconds gone by — a reading that takes twenty seconds should look like work, not like a hang.
function ProgresoDeLectura({ eventos, inicio }) {
  const [ahora, setAhora] = useState(() => Date.now());
  useEffect(() => {
    const reloj = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(reloj);
  }, []);
  const segundos = Math.max(0, Math.round((ahora - inicio) / 1000));

  return (
    <div className="grid gap-2">
      <ol
        aria-label="Progreso de la lectura"
        className="grid gap-1.5 rounded-[8px] border bg-brinks-surface px-3 py-2.5"
      >
        {pasosDeLectura(eventos).map((p) => (
          <li
            key={p.paso}
            data-estado={p.estado}
            className="flex items-start gap-2 text-[12.5px]"
          >
            <span className="mt-[2px] grid h-4 w-4 shrink-0 place-items-center">
              {ICONO_PASO[p.estado]}
            </span>
            <span className="min-w-0">
              <span
                className={cn(
                  p.estado === "pending"
                    ? "text-brinks-muted-foreground"
                    : "text-brinks-foreground",
                )}
              >
                {p.titulo}
              </span>
              {p.detalle ? (
                <span className="block text-[11.5px] text-brinks-muted-foreground">
                  {p.detalle}
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ol>
      <span className="text-[11.5px] text-brinks-muted-foreground">
        Tiempo transcurrido: {segundos} s
      </span>
    </div>
  );
}

// What perlita read off the picture, to confirm or correct before anything is saved. Opened as
// soon as the picture is sent, with the reading's progress until the proposal arrives.
function VistaPrevia({
  abierta,
  propuesta,
  lectura,
  existentes,
  hoy,
  guardando,
  error,
  onGuardar,
  onCerrar,
}) {
  const [filas, setFilas] = useState([]);
  useEffect(() => {
    if (propuesta) setFilas(filasDeVistaPrevia(propuesta.review));
  }, [propuesta]);

  const revisadas = revisarVistaPrevia(filas, { existentes, hoy });
  const aGuardar = fechasAGuardar(revisadas);
  const cambiar = (clave, cambios) =>
    setFilas((actuales) =>
      actuales.map((f) => (f.clave === clave ? { ...f, ...cambios } : f)),
    );

  return (
    <Dialog
      open={abierta}
      onOpenChange={(open) => !open && !guardando && !lectura && onCerrar()}
    >
      <DialogContent className="brinks-scope font-redhat max-w-xl">
        {lectura ? (
          <>
            <DialogHeader className="text-left">
              <div className="flex min-w-0 items-center gap-1">
                <DialogTitle className="text-[16px] font-semibold text-brinks-foreground">
                  Leyendo la imagen
                </DialogTitle>
                <BotonGuia guiaId="dialogo-leyendo-sabados" titulo="Leyendo la imagen" texto="La IA está leyendo los sábados de la imagen. Cuando termine verás cada fecha para revisarla; nada se guarda todavía." />
              </div>
              <DialogDescription className="text-[12.5px] text-brinks-muted-foreground">
                La IA está extrayendo los sábados. Cuando termine verás cada
                fecha para confirmarla o corregirla.
              </DialogDescription>
            </DialogHeader>
            <ProgresoDeLectura
              eventos={lectura.eventos}
              inicio={lectura.inicio}
            />
          </>
        ) : (
          <>
            <DialogHeader className="text-left">
              <div className="flex min-w-0 items-center gap-1">
                <DialogTitle className="text-[16px] font-semibold text-brinks-foreground">
                  Sábados leídos de la imagen
                </DialogTitle>
                <BotonGuia guiaId="dialogo-sabados-leidos" titulo="Sábados leídos de la imagen" texto="Las fechas que se leyeron de la imagen. Revisa cada una antes de guardar: nada se guarda hasta que confirmes." />
              </div>
              <DialogDescription className="text-[12.5px] text-brinks-muted-foreground">
                Revisa cada fecha antes de guardar: corrígela si se leyó mal o
                desmárcala si no va. Nada se guarda hasta que confirmes.
                {propuesta?.fileName ? ` Archivo: ${propuesta.fileName}.` : ""}
              </DialogDescription>
            </DialogHeader>

            {propuesta?.notes ? (
              <Aviso tono="aviso">{propuesta.notes}</Aviso>
            ) : null}

            {revisadas.length ? (
              <ul
                {...guia("Fechas leídas", "Cada fecha leída. Corrígela si se leyó mal o desmárcala si no va. En verde las que se guardan, en rojo las que no son un sábado válido.")}
                aria-label="Fechas leídas"
                className="grid max-h-[45vh] gap-1 overflow-y-auto rounded-[8px] border bg-brinks-surface p-1.5"
              >
                {revisadas.map((fila) => (
                  <li
                    key={fila.clave}
                    className={cn(
                      "grid grid-cols-[auto_150px_minmax(0,1fr)] items-center gap-2 rounded-[6px] px-2 py-1.5 text-[12.5px]",
                      fila.incluir && fila.ok ? "bg-card" : "opacity-80",
                    )}
                  >
                    <input
                      type="checkbox"
                      aria-label={`Incluir ${fila.leida || fila.fecha}`}
                      checked={fila.incluir}
                      disabled={guardando}
                      onChange={(e) =>
                        cambiar(fila.clave, { incluir: e.target.checked })
                      }
                      className="h-4 w-4 accent-[hsl(var(--secondary))]"
                    />
                    <input
                      type="date"
                      aria-label={`Fecha leída ${fila.leida}`}
                      value={fila.fecha}
                      disabled={guardando}
                      onChange={(e) =>
                        cambiar(fila.clave, {
                          fecha: e.target.value,
                          incluir: true,
                        })
                      }
                      className={cn(campo, "h-8")}
                    />
                    <span className="min-w-0">
                      {fila.incluir ? (
                        <span
                          className={cn(
                            "block truncate",
                            fila.ok
                              ? fila.aviso
                                ? "text-brinks-warning"
                                : "text-brinks-success"
                              : "text-brinks-danger",
                          )}
                        >
                          {fila.ok
                            ? fila.aviso || fechaLarga(fila.fecha)
                            : fila.mensaje}
                        </span>
                      ) : (
                        <span className="block truncate text-brinks-muted-foreground">
                          No se guardará
                          {fila.mensaje ? ` · ${fila.mensaje}` : ""}
                        </span>
                      )}
                      {fila.leida && fila.leida !== fila.fecha ? (
                        <span className="block truncate text-[11px] text-brinks-muted-foreground">
                          Leído: {fila.leida}
                        </span>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <Aviso tono="aviso">No se encontraron fechas en la imagen.</Aviso>
            )}

            <Aviso>{error}</Aviso>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                className="h-9"
                disabled={guardando}
                onClick={onCerrar}
              >
                Cancelar
              </Button>
              <Button
                className="h-9 gap-1.5"
                disabled={guardando || !aGuardar.length}
                {...guia("Guardar", "Guarda los sábados marcados. Desde ese momento cada uno abre el día de todas las oficinas.")}
                onClick={() => onGuardar(aGuardar)}
              >
                {guardando ? (
                  <Spinner size="sm" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                {aGuardar.length === 1
                  ? "Guardar 1 sábado"
                  : `Guardar ${aGuardar.length} sábados`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function SabadosLaborales({
  token,
  mantisBusinessId,
  clientCode,
  listo = true,
  puedeEditar,
  motivoSinPermiso,
}) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState("");
  const [vista, setVista] = useState("proximos");
  const [agregando, setAgregando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [aQuitar, setAQuitar] = useState(null);
  const [leyendo, setLeyendo] = useState(false);
  const [lectura, setLectura] = useState(null);
  const [propuesta, setPropuesta] = useState(null);
  const [errorVista, setErrorVista] = useState("");
  const entradaImagen = useRef(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setErrorCarga("");
    const response = await listCierreOpenDates(
      token,
      mantisBusinessId,
      clientCode,
      true,
    ).catch(() => null);
    if (response?.success) setDatos(response.data);
    else setErrorCarga(errorDeSabados(response));
    setCargando(false);
  }, [token, mantisBusinessId, clientCode]);

  useEffect(() => {
    if (!listo) return;
    // Another client: its list, not the last one's, decides whether the section shows.
    setDatos(null);
    setAgregando(false);
    setEditando(null);
    setMensaje(null);
    cargar();
  }, [listo, cargar]);

  const hoy = datos?.today;
  const existentes = useMemo(
    () => (datos?.dates || []).map((d) => d.date),
    [datos],
  );
  const { proximos, pasados } = useMemo(
    () => separarSabados(datos?.dates, hoy),
    [datos, hoy],
  );
  const filas =
    vista === "proximos"
      ? proximos
      : vista === "pasados"
        ? pasados
        : datos?.removed || [];
  const cuenta = {
    proximos: proximos.length,
    pasados: pasados.length,
    historial: (datos?.removed || []).length,
  };

  const tras = async (llamada, exito) => {
    setGuardando(true);
    setMensaje(null);
    const response = await llamada().catch(() => null);
    setGuardando(false);
    if (!response?.success) {
      setMensaje({ tono: "error", texto: errorDeSabados(response) });
      return false;
    }
    const aviso = (response.data?.warnings || []).flatMap((w) => w.messages)[0];
    setMensaje({
      tono: aviso ? "aviso" : "ok",
      texto: aviso ? `${exito} ${aviso}` : exito,
    });
    await cargar();
    return true;
  };

  const agregar = async (fecha, nota) => {
    const ok = await tras(
      () =>
        addCierreOpenDates(token, mantisBusinessId, clientCode, {
          dates: [fecha],
          source: "manual",
          note: nota,
        }),
      `Se agregó el ${fechaLarga(fecha)}.`,
    );
    if (ok) setAgregando(false);
  };

  const cambiar = async (sabado, fecha, nota) => {
    const ok = await tras(
      () =>
        updateCierreOpenDate(token, mantisBusinessId, clientCode, {
          id: sabado.id,
          date: fecha,
          note: nota,
        }),
      "Se guardó el cambio.",
    );
    if (ok) setEditando(null);
  };

  const quitar = async () => {
    const sabado = aQuitar;
    // Closed either way: a refusal is said in the section, where the dialog would hide it.
    await tras(
      () =>
        removeCierreOpenDate(token, mantisBusinessId, clientCode, sabado.id),
      `Se quitó el ${fechaLarga(sabado.date)}: ya no se programarán cierres ese día.`,
    );
    setAQuitar(null);
  };

  const leerImagen = async (archivo) => {
    if (!archivo) return;
    setLeyendo(true);
    setMensaje(null);
    setErrorVista("");
    setLectura({ inicio: Date.now(), eventos: [] });
    const response = await previewCierreOpenDates(
      token,
      mantisBusinessId,
      clientCode,
      archivo,
      (evento) =>
        setLectura((actual) =>
          actual ? { ...actual, eventos: [...actual.eventos, evento] } : actual,
        ),
    ).catch(() => null);
    setLeyendo(false);
    setLectura(null);
    if (response?.success) setPropuesta(response.data);
    else setMensaje({ tono: "error", texto: errorDeSabados(response) });
  };

  const guardarPropuesta = async (fechas) => {
    setGuardando(true);
    setErrorVista("");
    const response = await addCierreOpenDates(
      token,
      mantisBusinessId,
      clientCode,
      {
        dates: fechas,
        source: "image",
        sourceFile: propuesta?.fileName || undefined,
      },
    ).catch(() => null);
    setGuardando(false);
    if (!response?.success) {
      setErrorVista(errorDeSabados(response));
      return;
    }
    setPropuesta(null);
    setMensaje({
      tono: "ok",
      texto:
        fechas.length === 1
          ? "Se guardó 1 sábado."
          : `Se guardaron ${fechas.length} sábados.`,
    });
    await cargar();
  };

  // Nothing until the list arrives, and nothing for a client that does not work by listed
  // Saturdays — a box that appears and then vanishes on Davivienda's calendar would be noise.
  if (
    !errorCarga &&
    (!datos || (!datos.usesOpenDates && !(datos.dates || []).length))
  )
    return null;

  return (
    <section
      {...guia("Sábados laborales", "Los sábados que trabaja este cliente: cada uno abre el día de todas sus oficinas. \"Cargar desde imagen\" lee la lista del cliente; \"Agregar sábado\" añade uno a mano. En Pasados e Historial ves los anteriores y los cambios.")}
      aria-labelledby="sabados-titulo"
      className="overflow-hidden rounded-[12px] border bg-card shadow-brinks-card"
    >
      <header className="flex flex-wrap items-start justify-between gap-3 border-b p-3 sm:p-4">
        <div className="flex min-w-0 items-start gap-2.5">
          <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-[8px] bg-brinks-surface text-brinks-foreground">
            <CalendarCheck className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h2
              id="sabados-titulo"
              className="text-[15px] font-semibold text-brinks-foreground"
            >
              Sábados laborales
            </h2>
            <p className="mt-0.5 max-w-[640px] text-[12.5px] text-brinks-muted-foreground">
              Los sábados que el cliente trabaja. Cada uno abre el cierre de
              todas sus oficinas ese día; un sábado que no esté aquí no tiene
              cierre.
            </p>
          </div>
        </div>
        {puedeEditar ? (
          <div className="flex flex-wrap gap-2">
            <input
              ref={entradaImagen}
              type="file"
              accept="image/png,image/jpeg,image/webp,application/pdf"
              className="hidden"
              aria-label="Imagen con los sábados"
              onChange={(e) => {
                const [archivo] = e.target.files || [];
                e.target.value = "";
                leerImagen(archivo);
              }}
            />
            <Button
              variant="outline"
              size="sm"
              className="h-9 gap-1.5"
              disabled={leyendo || guardando || cargando}
              onClick={() => entradaImagen.current?.click()}
            >
              {leyendo ? (
                <Spinner size="sm" />
              ) : (
                <ImageUp className="h-4 w-4" />
              )}
              {leyendo ? "Leyendo la imagen…" : "Cargar desde imagen"}
            </Button>
            <Button
              size="sm"
              className="h-9 gap-1.5"
              disabled={agregando || guardando || cargando}
              onClick={() => {
                setAgregando(true);
                setEditando(null);
                setMensaje(null);
              }}
            >
              <Plus className="h-4 w-4" /> Agregar sábado
            </Button>
          </div>
        ) : null}
      </header>

      <div className="grid gap-2.5 p-3 sm:p-4">
        {!puedeEditar && motivoSinPermiso ? (
          <p className="text-[12px] text-brinks-muted-foreground">
            {motivoSinPermiso}
          </p>
        ) : null}

        {agregando ? (
          <div className="rounded-[10px] border border-dashed bg-brinks-surface p-2.5">
            <FormularioFecha
              existentes={existentes}
              hoy={hoy}
              guardando={guardando}
              etiqueta="Agregar"
              onGuardar={agregar}
              onCancelar={() => setAgregando(false)}
            />
          </div>
        ) : null}

        {mensaje ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}

        {cargando && !datos ? (
          <div className="grid place-items-center py-10">
            <Spinner size="lg" />
          </div>
        ) : errorCarga ? (
          <Aviso>{errorCarga}</Aviso>
        ) : (
          <>
            <div
              role="tablist"
              aria-label="Sábados"
              className="flex gap-1 border-b"
            >
              {VISTAS.map(({ clave, titulo }) => (
                <button
                  key={clave}
                  type="button"
                  role="tab"
                  aria-selected={vista === clave}
                  onClick={() => setVista(clave)}
                  className={cn(
                    "-mb-px flex items-center gap-1.5 border-b-2 px-2.5 py-1.5 text-[12.5px] transition-colors",
                    vista === clave
                      ? "border-brinks-foreground font-medium text-brinks-foreground"
                      : "border-transparent text-brinks-muted-foreground hover:text-brinks-foreground",
                  )}
                >
                  {clave === "historial" ? (
                    <History className="h-3.5 w-3.5" />
                  ) : null}
                  {titulo}
                  <span className="rounded-full bg-brinks-surface px-1.5 text-[11px] text-brinks-muted-foreground">
                    {cuenta[clave]}
                  </span>
                </button>
              ))}
            </div>

            {filas.length === 0 ? (
              <p className="py-8 text-center text-[12.5px] text-brinks-muted-foreground">
                {vista === "proximos"
                  ? "No hay sábados laborales próximos. Agrega los que el cliente va a trabajar."
                  : vista === "pasados"
                    ? "Todavía no hay sábados trabajados."
                    : "Nadie ha quitado ni cambiado sábados."}
              </p>
            ) : (
              <ul aria-label="Lista de sábados" className="divide-y">
                {filas.map((sabado) => {
                  const { dia, anio } = partesDeFecha(sabado.date);
                  const bloqueo = motivoBloqueo(sabado);
                  const enEdicion = editando === sabado.id;
                  return (
                    <li
                      key={sabado.id}
                      className="grid gap-2 py-2.5 sm:grid-cols-[96px_minmax(0,1fr)_auto] sm:items-center"
                    >
                      <div className="leading-tight">
                        <span className="block text-[15px] font-semibold capitalize text-brinks-foreground">
                          {dia}
                        </span>
                        <span className="text-[11.5px] text-brinks-muted-foreground">
                          sábado · {anio}
                        </span>
                      </div>
                      {enEdicion ? (
                        <FormularioFecha
                          inicial={sabado.date}
                          notaInicial={sabado.note || ""}
                          existentes={existentes.filter(
                            (f) => f !== sabado.date,
                          )}
                          hoy={hoy}
                          guardando={guardando}
                          etiqueta="Guardar"
                          fechaFija={bloqueo}
                          onGuardar={(fecha, nota) =>
                            cambiar(sabado, fecha, nota)
                          }
                          onCancelar={() => setEditando(null)}
                        />
                      ) : (
                        <div className="min-w-0 text-[12px] text-brinks-muted-foreground">
                          <span className="block truncate">
                            {origenDeSabado(sabado)} ·{" "}
                            {vista === "historial" ? "agregado" : "por"}{" "}
                            {sabado.createdBy}
                            {sabado.createdAt
                              ? `, ${cuando(sabado.createdAt)}`
                              : ""}
                          </span>
                          {vista === "historial" ? (
                            <span className="block truncate">
                              Quitado por {sabado.deletedBy},{" "}
                              {cuando(sabado.deletedAt)}
                            </span>
                          ) : null}
                          {sabado.note ? (
                            <span className="block truncate text-brinks-foreground">
                              {sabado.note}
                            </span>
                          ) : null}
                        </div>
                      )}
                      {vista !== "historial" && !enEdicion ? (
                        <div className="flex items-center gap-1.5 sm:justify-end">
                          {sabado.closings ? (
                            <span className="rounded-full border border-brinks-success-border bg-brinks-success-soft px-2 py-0.5 text-[11.5px] text-brinks-success">
                              {sabado.closings} cierres
                            </span>
                          ) : (
                            <span className="rounded-full border px-2 py-0.5 text-[11.5px] text-brinks-muted-foreground">
                              Sin cierres
                            </span>
                          )}
                          {puedeEditar ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                aria-label={`Cambiar ${fechaLarga(sabado.date)}`}
                                title={
                                  bloqueo
                                    ? "Cambiar la nota"
                                    : "Cambiar fecha o nota"
                                }
                                disabled={guardando}
                                onClick={() => {
                                  setEditando(sabado.id);
                                  setAgregando(false);
                                  setMensaje(null);
                                }}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-brinks-danger"
                                aria-label={`Quitar ${fechaLarga(sabado.date)}`}
                                title={bloqueo || "Quitar de la lista"}
                                disabled={guardando || Boolean(bloqueo)}
                                onClick={() => setAQuitar(sabado)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          ) : null}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>

      <VistaPrevia
        abierta={Boolean(propuesta || lectura)}
        propuesta={propuesta}
        lectura={lectura}
        existentes={existentes}
        hoy={hoy}
        guardando={guardando}
        error={errorVista}
        onGuardar={guardarPropuesta}
        onCerrar={() => {
          setPropuesta(null);
          setErrorVista("");
        }}
      />

      <Dialog
        open={Boolean(aQuitar)}
        onOpenChange={(open) => !open && !guardando && setAQuitar(null)}
      >
        <DialogContent className="brinks-scope font-redhat max-w-md">
          <DialogHeader className="text-left">
            <div className="flex min-w-0 items-center gap-1">
              <DialogTitle className="text-[16px] font-semibold text-brinks-foreground">
                Quitar el {fechaLarga(aQuitar?.date)}
              </DialogTitle>
              <BotonGuia guiaId="dialogo-quitar-sabado" titulo="Quitar un sábado" texto="Ese sábado deja de programarse y ninguna oficina tendrá cierre ese día. Queda en el historial quién lo quitó y cuándo." />
            </div>
            <DialogDescription className="text-[12.5px] text-brinks-muted-foreground">
              Ese día dejará de programarse: ninguna oficina tendrá cierre.
              Queda en el historial quién lo quitó y cuándo.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="h-9"
              disabled={guardando}
              onClick={() => setAQuitar(null)}
            >
              Cancelar
            </Button>
            <Button
              className="h-9 gap-1.5"
              disabled={guardando}
              onClick={quitar}
            >
              {guardando ? <Spinner size="sm" /> : <X className="h-4 w-4" />}{" "}
              Quitar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default SabadosLaborales;
